import { Search, Trash2 } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { useNavigate, useSearchParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableCard, TableCardField, TableCardList } from '@/components/ui/table-card'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { SalesOwnerFilter } from '../../../../components/SalesOwnerFilter'
import { useDebouncedInput } from '../../../../hooks/useDebouncedInput'
import { useVendors } from '../../../../hooks/useVendors'
import { InsuranceApplicationSource } from '../../../../models/insuranceApplication'
import { Vendor } from '../../../../models/insuranceTypes'
import {
  CarrierStatus,
  carrierStatusLabels,
  type InternalApplication,
  useDeleteInternalApplication,
  useInternalApplications,
} from '../../../../models/internalApplication'
import { CarrierStatusList } from './CarrierStatusList'

const SOURCE_LABELS: Record<InsuranceApplicationSource, string> = {
  [InsuranceApplicationSource.WEB]: 'Web',
  [InsuranceApplicationSource.EMAIL]: 'Email',
  [InsuranceApplicationSource.SMS]: 'SMS',
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function Applications() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { vendors } = useVendors()

  const page = Number(searchParams.get('page') || '1')
  const search = searchParams.get('search') || ''
  const has = searchParams.get('has') || ''
  const vendorsParam = searchParams.get('vendors') || ''
  const carrierStatusParam = searchParams.get('carrier_status') || ''
  const sourceParam = searchParams.get('source') || ''
  const salesOwnerParam = searchParams.get('sales_owner') || ''

  function updateParams(updates: Record<string, string | undefined>) {
    const params: Record<string, string> = {}
    // Preserve existing params
    if (search) params.search = search
    if (has) params.has = has
    if (vendorsParam) params.vendors = vendorsParam
    if (carrierStatusParam) params.carrier_status = carrierStatusParam
    if (sourceParam) params.source = sourceParam
    if (salesOwnerParam) params.sales_owner = salesOwnerParam
    // Apply updates
    for (const [key, value] of Object.entries(updates)) {
      if (value) {
        params[key] = value
      } else {
        delete params[key]
      }
    }
    // Reset page when filters change (unless the update is a page change)
    if (!('page' in updates)) {
      delete params.page
    }
    setSearchParams(params)
  }

  const { inputValue, setInputValue } = useDebouncedInput(search, (value) =>
    updateParams({ search: value || undefined })
  )

  const { applications, count, hasNextPage, hasPreviousPage, isLoading } = useInternalApplications({
    page,
    search: search || undefined,
    vendors: vendorsParam ? ([vendorsParam] as Vendor[]) : undefined,
    carrierStatus: carrierStatusParam ? (carrierStatusParam as CarrierStatus) : undefined,
    has: has || undefined,
    source: sourceParam || undefined,
    salesOwner: salesOwnerParam || undefined,
  })
  const deleteApplication = useDeleteInternalApplication()

  function goToPage(p: number) {
    updateParams({ page: p > 1 ? String(p) : undefined })
  }

  function deleteApplicationDialog(app: InternalApplication) {
    return (
      <ConfirmationDialog
        title="Delete application"
        description={`Delete application for ${app.orgName} (DOT ${app.dotNumber || '—'})? Indications, quotes, policies, chat, and documents will be removed. Drivers, trucks, and users are preserved.`}
        confirmLabel="Delete"
        onConfirm={() => deleteApplication.mutateAsync(app.id)}
      >
        <Button variant="ghost" size="icon" aria-label="Delete application">
          <Trash2 className="size-4 text-red-500" />
        </Button>
      </ConfirmationDialog>
    )
  }

  return (
    <>
      <Helmet>
        <title>Applications</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div className="mb-2 text-2xl font-semibold">Applications</div>
        <Card>
          <CardHeader className="px-4 sm:px-6">
            <CardTitle>Insurance Applications</CardTitle>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="relative w-full sm:max-w-sm sm:min-w-64 sm:flex-1">
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  placeholder="Search by DOT/MC, org, carrier or legal name, or user..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="h-11 border-neutral-300 pl-9 text-sm"
                />
              </div>

              <Select
                value={has || 'all'}
                onValueChange={(v) => updateParams({ has: v === 'all' ? undefined : v })}
              >
                <SelectTrigger className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[160px]">
                  <SelectValue placeholder="Record Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Records</SelectItem>
                  <SelectItem value="indications">Indications</SelectItem>
                  <SelectItem value="quotes">Quotes</SelectItem>
                  <SelectItem value="policies">Policies</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={vendorsParam || 'all'}
                onValueChange={(v) => updateParams({ vendors: v === 'all' ? undefined : v })}
              >
                <SelectTrigger className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[160px]">
                  <SelectValue placeholder="Carrier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All carriers</SelectItem>
                  {vendors.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* With a carrier picked, matches where that carrier stands; without one, any carrier. */}
              <Select
                value={carrierStatusParam || 'all'}
                onValueChange={(v) => updateParams({ carrier_status: v === 'all' ? undefined : v })}
              >
                <SelectTrigger className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[170px]">
                  <SelectValue placeholder="Carrier status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.values(CarrierStatus).map((status) => (
                    <SelectItem key={status} value={status}>
                      {carrierStatusLabels[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={sourceParam || 'all'}
                onValueChange={(v) => updateParams({ source: v === 'all' ? undefined : v })}
              >
                <SelectTrigger className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[160px]">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {Object.values(InsuranceApplicationSource).map((source) => (
                    <SelectItem key={source} value={source}>
                      {SOURCE_LABELS[source]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <SalesOwnerFilter
                value={salesOwnerParam}
                onValueChange={(v) => updateParams({ sales_owner: v === 'all' ? undefined : v })}
                className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[180px]"
              />
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            {/* The table doesn't fit a phone, so under `lg` each application becomes a card. */}
            <div className="lg:hidden">
              {isLoading && <SkeletonRows rows={6} />}
              {!isLoading && applications.length === 0 && (
                <div className="text-muted-foreground py-5 text-center text-sm">No applications found</div>
              )}
              {!isLoading && applications.length > 0 && (
                <TableCardList>
                  {applications.map((app) => (
                    <TableCard
                      key={app.id}
                      title={app.orgName}
                      onOpen={() => navigate(`/insure/internal/applications/${app.id}`)}
                      actions={deleteApplicationDialog(app)}
                    >
                      <TableCardField label="DOT Number" value={app.dotNumber} />
                      <TableCardField
                        label="Source"
                        value={
                          <Badge variant="outline" className="text-xs">
                            {SOURCE_LABELS[app.source] || app.source}
                          </Badge>
                        }
                      />
                      <TableCardField label="Date" value={formatDate(app.dateCreated)} />
                      <TableCardField
                        label="Carriers"
                        value={<CarrierStatusList carriers={app.carriers} />}
                      />
                    </TableCard>
                  ))}
                </TableCardList>
              )}
            </div>
            <div className="max-lg:hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Org Name</TableHead>
                    <TableHead className="font-semibold">DOT Number</TableHead>
                    <TableHead className="font-semibold">Source</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Carriers</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <TableSkeletonRows columns={6} />}
                  {!isLoading && applications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="pt-5 text-center">
                        No applications found
                      </TableCell>
                    </TableRow>
                  )}
                  {applications.map((app) => (
                    <TableRow
                      key={app.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/insure/internal/applications/${app.id}`)}
                    >
                      <TableCell className="font-medium">{app.orgName}</TableCell>
                      <TableCell>{app.dotNumber || '\u2014'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {SOURCE_LABELS[app.source] || app.source}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(app.dateCreated)}</TableCell>
                      <TableCell>
                        <CarrierStatusList carriers={app.carriers} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {deleteApplicationDialog(app)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {(hasPreviousPage || hasNextPage) && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-muted-foreground text-base">
                  Page {page} ({count} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasPreviousPage}
                    onClick={() => goToPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasNextPage}
                    onClick={() => goToPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
